/**
 * The tabs of the settings pages that have them, and the `?tab=` that picks one (M286 ideas 12, 14, 16, 17): a link, a
 * redirect (/settings/widget-permissions, /settings/reference-data, …) or settings search lands on its tab. Kept here,
 * not beside the pages, because settings search lists them too.
 */
import type React from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, Settings, UserPlus, Users } from 'lucide-react';
import type { SettingsCrumb } from './SettingsPageShell';

export type PermissionTabId = 'roles' | 'users' | 'features' | 'widgets';

// Permissions + Permission Matrix tabs are intentionally omitted: the app's runtime gating is feature-only (see
// useFeatureAccess), so the granular `*:create / *:edit / ...` permission rows are not consulted anywhere. Re-add
// these tabs the day we wire `RequirePermission` around CRUD buttons + backend routes.
export const PERMISSION_TABS: ReadonlyArray<{ id: PermissionTabId; label: string; icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean }> = [
  { id: 'roles', label: 'Role Definitions', icon: Users },
  { id: 'users', label: 'User Roles (read-only)', icon: UserPlus },
  { id: 'features', label: 'Feature Access', icon: Settings },
  { id: 'widgets', label: 'Widget Permissions', icon: LayoutGrid, adminOnly: true },
];

/** Data curation's tabs (M286 idea 16): the three pages it replaced, each behind its old gate (useDataCurationTabs). */
export type DataCurationTabId = 'lookups' | 'types' | 'fields';

export const DATA_CURATION_PATH = '/settings/data-curation';

export const DATA_CURATION_TABS: ReadonlyArray<{ id: DataCurationTabId; label: string }> = [
  { id: 'lookups', label: 'Reference Data' },
  { id: 'types', label: 'Property Types & Lease Lookups' },
  { id: 'fields', label: 'Property-Type Fields & Features' },
];

/** Settings → AI's tabs (M286 idea 17): AI Model Settings, AI Lab and AI Spend, once three pages, and the ai.* settings groups. */
export type AiTabId = 'models' | 'lab' | 'spend' | 'caps';

export const AI_PATH = '/settings/ai';

export const AI_TABS: ReadonlyArray<{ id: AiTabId; label: string }> = [
  { id: 'models', label: 'Models' },
  { id: 'lab', label: 'Lab' },
  { id: 'spend', label: 'Spend' },
  { id: 'caps', label: 'Caps & corrections' },
];

/** The settings groups on the Caps & corrections tab (`editedOn` in app-config, so not on App settings). */
export const AI_CAPS_GROUPS = ['ai.lab', 'ai.corrections'] as const;

/** The breadcrumb above the lab's run and variant pages, which keep their own URLs: back to the Lab tab. */
export const AI_LAB_CRUMBS: SettingsCrumb[] = [{ label: 'AI Lab', to: `${AI_PATH}?tab=lab` }];

export type PropertyTypesTabId = 'property' | 'lease_subtypes' | 'rent_structures';

/** Property Types' own tabs sit inside Data curation's `types` tab, so they take `?sub=`, not `?tab=`. */
export const PROPERTY_TYPES_TAB_PARAM = 'sub';

export const PROPERTY_TYPES_TABS: ReadonlyArray<{ id: PropertyTypesTabId; label: string }> = [
  { id: 'property', label: 'Property Types & Uses' },
  { id: 'lease_subtypes', label: 'Lease Types' },
  { id: 'rent_structures', label: 'Additional Lease Types' },
];

/** The one of `ids` that `?tab=` (or `?<param>=`) names (the first when it names none of them), and a setter; the first tab has no param. */
export function useTabParam<T extends string>(ids: readonly T[], param = 'tab'): [T, (id: T) => void] {
  const [params, setParams] = useSearchParams();
  const asked = params.get(param);
  const active = ids.find((id) => id === asked) ?? ids[0];
  const setActive = (id: T) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === ids[0]) next.delete(param);
        else next.set(param, id);
        return next;
      },
      { replace: true },
    );
  return [active, setActive];
}
